import { createSlice } from '@reduxjs/toolkit';
import {
  IMockRun,
  IMockRunCapacityData,
  MockAllocationDetails,
  ValidationData
} from '../interface';
import { AllocationExperiment } from '../components/CaseExclusionExperiment/interfaces';
import { MockControlState } from '../types';

interface MockControlAction {
  state: MockControlState;
  message: string | null;
}

interface MockControlsStatus {
  initiateMock: MockControlAction;
  triggerMock: MockControlAction;
  approveMock: MockControlAction;
  monthEndDeadlineEpochMs?: number;
}

interface FieldAllocationState {
  mockRunsMap: Record<string, IMockRun>;
  mockRunsListLoading: boolean;
  inputValidations: {
    loading: boolean;
    data: ValidationData[];
    lastUpdated: number;
  };
  initiateMockLoading: boolean;
  fieldAllocationStatusLoading: boolean;
  mockApprovalStatusLoading: boolean;
  fieldAllocationDetails: MockAllocationDetails | null;
  mockRunCapacity: Record<string, IMockRunCapacityData>;
  previousAllocationDetails: MockAllocationDetails | null;
  previousAllocationStatusLoading: boolean;
  updateMockRunStatusLoading: boolean;
  experimentsList: AllocationExperiment[];
  experimentsListLoading: boolean;
  experimentData: AllocationExperiment | null;
  isLoading: boolean;
  failureReport: string | null;
  mockControlsStatus: MockControlsStatus | null;
}

const initialState: FieldAllocationState = {
  mockRunsMap: {},
  mockRunsListLoading: false,
  inputValidations: {
    loading: false,
    lastUpdated: 0,
    data: []
  },
  initiateMockLoading: false,
  fieldAllocationStatusLoading: false,
  mockApprovalStatusLoading: false,
  fieldAllocationDetails: null,
  mockRunCapacity: {},
  previousAllocationDetails: null,
  previousAllocationStatusLoading: false,
  updateMockRunStatusLoading: false,
  experimentsList: [],
  experimentsListLoading: false,
  isLoading: false,
  experimentData: null,
  failureReport: null,
  mockControlsStatus: null
};

const fieldAllocationSlice = createSlice({
  name: 'fieldAllocation',
  initialState,
  reducers: {
    setMockRunsMap: (state, action) => {
      const data = action.payload;
      const mockRunsMap: Record<string, IMockRun> = {};
      data?.forEach((mockRun: IMockRun) => {
        if (mockRun?.allocationTrackerId) {
          mockRunsMap[mockRun.allocationTrackerId] = mockRun;
        }
      });
      state.mockRunsMap = mockRunsMap;
    },
    setMockRunsListLoading: (state, action) => {
      state.mockRunsListLoading = action.payload;
    },
    setInputValidations: (state, action) => {
      const inputValidationsData = action.payload.data;
      state.inputValidations.data = Object.values(inputValidationsData) as [];
      state.inputValidations.lastUpdated = action.payload.lastUpdated;
    },
    setInputValidationsLoading: (state, action) => {
      state.inputValidations.loading = action.payload;
    },
    setInitiateMockLoading: (state, action) => {
      state.initiateMockLoading = action.payload;
    },
    setFieldAllocationStatusLoading: (state, action) => {
      state.fieldAllocationStatusLoading = action.payload;
    },
    setMockAllocationDetails: (state, action) => {
      state.fieldAllocationDetails = action.payload;
    },
    setMockApprovalStatusLoading: (state, action) => {
      state.mockApprovalStatusLoading = action.payload;
    },
    setMockRunCapacity: (state, action) => {
      state.mockRunCapacity = action.payload;
    },
    setPreviousAllocationDetails: (state, action) => {
      state.previousAllocationDetails = action.payload;
    },
    setPreviousAllocationStatusLoading: (state, action) => {
      state.previousAllocationStatusLoading = action.payload;
    },
    setUpdateMockRunStatusLoading: (state, action) => {
      state.updateMockRunStatusLoading = action.payload;
    },
    setExperimentsList: (state, action) => {
      state.experimentsList = action.payload;
    },
    setExperimentListLoading: (state, action) => {
      state.experimentsListLoading = action.payload;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setExperimentData: (state, action) => {
      state.experimentData = action.payload;
    },
    setFailureReport: (state, action) => {
      state.failureReport = action.payload;
    },
    setMockControlsStatus: (state, action) => {
      state.mockControlsStatus = action.payload;
    }
  }
});

export const {
  setMockRunsMap,
  setMockRunsListLoading,
  setInputValidations,
  setInputValidationsLoading,
  setInitiateMockLoading,
  setFieldAllocationStatusLoading,
  setMockAllocationDetails,
  setMockApprovalStatusLoading,
  setMockRunCapacity,
  setPreviousAllocationDetails,
  setPreviousAllocationStatusLoading,
  setUpdateMockRunStatusLoading,
  setExperimentsList,
  setExperimentListLoading,
  setIsLoading,
  setExperimentData,
  setFailureReport,
  setMockControlsStatus
} = fieldAllocationSlice.actions;

export default fieldAllocationSlice.reducer;
